/**
 * Охрана труда в разделе «Качество и ОТ» (поток 7): допуски и удостоверения работников (КЧ-07), наряды-допуски
 * (КЧ-05), инструктажи (КЧ-06) и наблюдения за безопасностью (КЧ-09).
 */
import { z } from 'zod';

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата ГГГГ-ММ-ДД');
const Moment = z.iso.datetime({ offset: true });
const Text = (max: number) => z.string().trim().min(1).max(max);

/* ─────────── Допуски и удостоверения работников (КЧ-07) ─────────── */

export const CREDENTIAL_KINDS = {
  ot: 'Охрана труда (обучение, проверка знаний)',
  pb: 'Пожарная безопасность',
  electro: 'Группа по электробезопасности',
  height: 'Работы на высоте',
  rigger: 'Стропальщик',
  welder: 'Сварщик',
  operator: 'Машинист, оператор',
  other: 'Другое удостоверение',
} as const;
export type CredentialKind = keyof typeof CREDENTIAL_KINDS;
const CredentialKindEnum = z.enum(Object.keys(CREDENTIAL_KINDS) as [CredentialKind, ...CredentialKind[]]);

export const CredentialInput = z
  .strictObject({
    workerId: z.uuid(),
    kind: CredentialKindEnum,
    /** Группа, разряд, класс — «III», «5 разряд» */
    grade: z.string().trim().max(60).nullable().optional(),
    number: z.string().trim().max(80).nullable().optional(),
    issuer: z.string().trim().max(200).nullable().optional(),
    issuedOn: Day.nullable().optional(),
    validUntil: Day,
  })
  .refine((v) => !v.issuedOn || v.issuedOn <= v.validUntil, {
    message: 'Срок действия раньше даты выдачи',
    path: ['validUntil'],
  });
export type CredentialInput = z.infer<typeof CredentialInput>;

/** valid — действует; expiring — истекает в ближайшие 30 дней; expired — истёк; revoked — аннулирован */
export type CredentialState = 'valid' | 'expiring' | 'expired' | 'revoked';

export interface CredentialRow {
  id: string;
  workerId: string;
  workerName: string;
  counterpartyName: string | null;
  projectId: string | null;
  kind: CredentialKind;
  grade: string | null;
  number: string | null;
  issuer: string | null;
  issuedOn: string | null;
  validUntil: string;
  state: CredentialState;
  daysLeft: number;
  revokeReason: string | null;
}

export interface SafetyWorker {
  id: string;
  fullName: string;
  counterpartyName: string | null;
  hasAccount: boolean;
  /** Действующие виды допусков */
  validKinds: CredentialKind[];
  expiredCount: number;
  expiringCount: number;
}

/** Состояние допуска на день: срок включительно */
export function credentialState(
  validUntil: string,
  today: string,
  revoked: boolean,
  soonDays = 30,
): { state: CredentialState; daysLeft: number } {
  const days = Math.round(
    (Date.parse(`${validUntil}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  if (revoked) return { state: 'revoked', daysLeft: days };
  if (days < 0) return { state: 'expired', daysLeft: days };
  return { state: days <= soonDays ? 'expiring' : 'valid', daysLeft: days };
}

/* ─────────── Наряды-допуски (КЧ-05) ─────────── */

export const PERMIT_KINDS = {
  hot: { title: 'Огневые работы', requires: ['pb'], approver: true },
  height: { title: 'Работы на высоте', requires: ['height'], approver: false },
  earth: { title: 'Земляные работы', requires: [], approver: true },
  gas: { title: 'Газоопасные работы', requires: [], approver: true },
  power_line: { title: 'Работы вблизи ЛЭП', requires: ['electro'], approver: true },
  crane: { title: 'Подъём краном', requires: ['rigger'], approver: false },
} as const satisfies Record<string, { title: string; requires: CredentialKind[]; approver: boolean }>;
export type PermitKind = keyof typeof PERMIT_KINDS;
const PermitKindEnum = z.enum(Object.keys(PERMIT_KINDS) as [PermitKind, ...PermitKind[]]);

/**
 * draft — готовится; signing — идёт маршрут подписей; signed — подписан, ждёт бригаду и открытия; active — работы идут;
 * suspended — работы остановлены (превышение в анализе воздуха или вручную), возобновляются после проверки;
 * done — работы завершены, бригада выведена: снимаются изоляции, затем наряд закрывается
 */
export const PERMIT_STATUSES = {
  draft: 'Готовится',
  signing: 'На подписи',
  signed: 'Подписан',
  active: 'Действует',
  suspended: 'Работы остановлены',
  done: 'Работы завершены',
  closed: 'Закрыт',
  cancelled: 'Отменён',
} as const;
export type PermitStatus = keyof typeof PERMIT_STATUSES;

export function permitNumber(n: number): string {
  return `НД-${String(n).padStart(4, '0')}`;
}

export const PermitInput = z
  .strictObject({
    projectId: z.uuid(),
    objectId: z.uuid().nullable().optional(),
    activityId: z.uuid().nullable().optional(),
    contractorId: z.uuid().nullable().optional(),
    kind: PermitKindEnum,
    title: Text(300),
    place: z.string().trim().max(300).nullable().optional(),
    measures: z.string().trim().max(4000).nullable().optional(),
    responsibleId: z.uuid(),
    approverId: z.uuid().nullable().optional(),
    issuerId: z.uuid(),
    validFrom: Moment,
    validUntil: Moment,
    /** Обоснование одновременных работ (SIMOPS) и дополнительные меры, если рядом в то же время опасные работы */
    simopsNote: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((v) => Date.parse(v.validUntil) > Date.parse(v.validFrom), {
    message: 'Срок действия кончается раньше начала',
    path: ['validUntil'],
  })
  .refine((v) => Date.parse(v.validUntil) - Date.parse(v.validFrom) <= 31 * 86_400_000, {
    message: 'Наряд-допуск выдаётся не больше чем на 31 день',
    path: ['validUntil'],
  });
export type PermitInput = z.infer<typeof PermitInput>;

export interface PermitCrewView {
  workerId: string;
  fullName: string;
  counterpartyName: string | null;
  hasAccount: boolean;
  acknowledgedAt: string | null;
  paper: boolean;
  /** Чего не хватает по допускам: пусто — можно ставить в наряд */
  missing: CredentialKind[];
  /** Этот человек — я (могу подтвердить ознакомление в приложении) */
  isMe: boolean;
}

export interface PermitSignView {
  step: number;
  role: 'responsible' | 'approver' | 'issuer';
  userId: string;
  userName: string;
  signedAt: string | null;
  comment: string | null;
  /** Отпечаток содержания (SHA-256), под которым поставлена подпись */
  docHash: string | null;
}

export interface PermitSummary {
  id: string;
  number: string;
  projectId: string;
  projectCode: string;
  kind: PermitKind;
  title: string;
  place: string | null;
  status: PermitStatus;
  /** Действующий, но срок вышел: наряд не даёт права работать */
  expired: boolean;
  validFrom: string;
  validUntil: string;
  responsibleName: string;
  crewCount: number;
}

export interface PermitDetail extends PermitSummary {
  objectId: string | null;
  objectName: string | null;
  activityId: string | null;
  contractorId: string | null;
  contractorName: string | null;
  measures: string | null;
  responsibleId: string;
  approverId: string | null;
  approverName: string | null;
  issuerId: string;
  issuerName: string;
  approverRequired: boolean;
  requires: CredentialKind[];
  signs: PermitSignView[];
  crew: PermitCrewView[];
  openedAt: string | null;
  closedAt: string | null;
  closeNote: string | null;
  cancelReason: string | null;
  /** Что мешает открыть наряд сейчас */
  blockers: string[];
  /** Что я могу сделать сейчас */
  can: {
    edit: boolean;
    submit: boolean;
    sign: boolean;
    open: boolean;
    close: boolean;
    cancel: boolean;
    /** Внести анализ воздуха */
    gas: boolean;
    suspend: boolean;
    resume: boolean;
    /** Добавлять и убирать точки изоляции (пока наряд готовится) */
    isolations: boolean;
    /** Отмечать установку, проверку и снятие изоляций */
    isolationMark: boolean;
    /** Продлить наряд (подпись выдавшего) */
    extend: boolean;
    /** Отметить завершение работ */
    complete: boolean;
    /** Править анализ рисков JSA (пока наряд готовится) */
    risks: boolean;
  };
  briefings: Array<{ id: string; number: string; heldOn: string; status: 'open' | 'closed' }>;
  suspendedAt: string | null;
  suspendReason: string | null;
  /** Что мешает возобновить остановленные работы */
  resumeBlockers: string[];
  /** Наименьшая бригада по настройкам вида работ */
  minCrew: number;
  gas: PermitGasView;
  events: PermitEventView[];
  isolations: PermitIsolationView[];
  /** Нужны ли изоляции по виду работ (настройки компании) */
  lotoRequired: boolean;
  /** Способы изоляции для формы — из настроек компании */
  isolationMethods: string[];
  simops: PermitSimopsView[];
  simopsNote: string | null;
  /** Что мешает закрыть наряд */
  closeBlockers: string[];
  /** Наибольший срок наряда по настройкам, дней — для продления */
  maxDays: number;
  /** Анализ рисков JSA по шагам работы */
  risks: PermitRiskView[];
  /** Нужен ли анализ рисков по виду работ (настройки компании) */
  jsaRequired: boolean;
  /** Границы уровней риска из настроек компании */
  riskLimits: RiskLimits;
  /** Отпечаток содержания SHA-256, зафиксированный при отправке на подпись (и при продлении) */
  contentHash: string | null;
  /** Содержание совпадает с отпечатком: true — не менялось, false — изменилось, null — ещё не отправлялся */
  hashOk: boolean | null;
}

/* ─────────── Анализ рисков JSA 5 × 5 (КЧ-05) ─────────── */

/** Границы уровней риска (тяжесть × вероятность, 1–25): от `medium` — средний, от `high` — высокий, от `unacceptable` — недопустимый */
export const RiskLimits = z
  .strictObject({
    medium: z.number().int().min(2).max(25),
    high: z.number().int().min(2).max(25),
    unacceptable: z.number().int().min(2).max(25),
  })
  .refine((v) => v.medium < v.high && v.high < v.unacceptable, {
    message: 'Границы уровней риска идут по возрастанию: средний < высокий < недопустимый',
    path: ['high'],
  });
export type RiskLimits = z.infer<typeof RiskLimits>;
export const RISK_LIMITS_DEFAULT: RiskLimits = { medium: 5, high: 10, unacceptable: 15 };

export type RiskLevel = 'low' | 'medium' | 'high' | 'unacceptable';
export const RISK_LEVELS: Record<RiskLevel, string> = {
  low: 'Низкий',
  medium: 'Средний',
  high: 'Высокий',
  unacceptable: 'Недопустимый',
};

export function riskLevel(score: number, limits: RiskLimits): RiskLevel {
  if (score >= limits.unacceptable) return 'unacceptable';
  if (score >= limits.high) return 'high';
  if (score >= limits.medium) return 'medium';
  return 'low';
}

/** Тяжесть последствий и вероятность — по шкале 1–5 */
export const RISK_SEVERITY: Record<number, string> = {
  1: 'Незначительная — без травмы',
  2: 'Малая — первая помощь',
  3: 'Средняя — травма с потерей трудоспособности',
  4: 'Тяжёлая — тяжёлая травма, авария',
  5: 'Катастрофическая — гибель людей',
};
export const RISK_LIKELIHOOD: Record<number, string> = {
  1: 'Почти невозможно',
  2: 'Маловероятно',
  3: 'Возможно',
  4: 'Вероятно',
  5: 'Почти наверняка',
};

const Scale = z.number().int().min(1).max(5);
export const PermitRiskInput = z
  .strictObject({
    /** Шаг работы: «Вскрытие люка резервуара» */
    step: Text(250),
    /** Опасность: «Выброс сероводорода» */
    hazard: Text(250),
    severity: Scale,
    likelihood: Scale,
    /** Меры управления риском */
    controls: Text(2000),
    /** Оценка после мер */
    residualSeverity: Scale,
    residualLikelihood: Scale,
  })
  .refine((v) => v.residualSeverity * v.residualLikelihood <= v.severity * v.likelihood, {
    message: 'Остаточный риск после мер не может быть выше исходного',
    path: ['residualLikelihood'],
  });
export type PermitRiskInput = z.infer<typeof PermitRiskInput>;

export interface PermitRiskView {
  id: string;
  position: number;
  step: string;
  hazard: string;
  severity: number;
  likelihood: number;
  score: number;
  level: RiskLevel;
  controls: string;
  residualSeverity: number;
  residualLikelihood: number;
  residual: number;
  residualLevel: RiskLevel;
}

/* ─────────── Изоляции LOTO, одновременные работы, продление (КЧ-05, КЧ-20) ─────────── */

/** planned — запланирована; installed — установлена, не проверена; verified — проверена вторым человеком; removed — снята */
export type IsolationState = 'planned' | 'installed' | 'verified' | 'removed';
export const ISOLATION_STATES: Record<IsolationState, string> = {
  planned: 'Запланирована',
  installed: 'Установлена, не проверена',
  verified: 'Установлена и проверена',
  removed: 'Снята',
};

export const PERMIT_ISOLATION_METHODS_DEFAULT = [
  'Закрыта арматура (кран, задвижка) и опломбирована',
  'Установлена заглушка',
  'Замок и бирка «Не включать»',
  'Электрическое отключение, разобрана схема',
  'Сброс давления, продувка, дренаж',
];

export const IsolationInput = z.strictObject({
  /** Точка изоляции: например, «задвижка № 3 на входе резервуара» */
  point: Text(200),
  method: Text(200),
  /** Номер замка, бирки или заглушки */
  tag: z.string().trim().max(60).nullable().optional(),
});
export type IsolationInput = z.infer<typeof IsolationInput>;

export const IsolationMarkInput = z.strictObject({ action: z.enum(['install', 'verify', 'remove']) });

export interface PermitIsolationView {
  id: string;
  point: string;
  method: string;
  tag: string | null;
  state: IsolationState;
  installedAt: string | null;
  installedByName: string | null;
  verifiedAt: string | null;
  verifiedByName: string | null;
  removedAt: string | null;
  removedByName: string | null;
  /** Что мешает снять изоляцию сейчас */
  removeBlockers: string[];
  /** Могу отметить проверку: изоляцию установил не я */
  canVerify: boolean;
}

/** Одновременные работы: другой наряд проекта, пересекающийся по времени */
export interface PermitSimopsView {
  permitId: string;
  number: string;
  kind: PermitKind;
  title: string;
  status: PermitStatus;
  /** bad — нужно обоснование перед отправкой; warn — обратить внимание */
  level: 'bad' | 'warn';
  reason: string;
}

export const PermitExtendInput = z.strictObject({
  validUntil: Moment,
  reason: z.string().trim().min(3).max(500),
  /** Обоснование одновременных работ, если за счёт продления рядом оказались опасные работы */
  simopsNote: z.string().trim().max(2000).nullable().optional(),
});
export const PermitCompleteInput = z.strictObject({
  note: z.string().trim().max(1000).nullable().optional(),
});

/** Ключ точки изоляции для сравнения между нарядами: без регистра, «№» и лишних пробелов */
export function isolationKey(point: string): string {
  return point.toLowerCase().replace(/№/g, ' ').split(/\s+/).filter(Boolean).join(' ');
}

/* ─────────── Настройки нарядов компании и анализ воздуха (КЧ-05) ─────────── */

/** Ключ настройки компании (core.tenant_settings) */
export const PERMIT_SETTINGS_KEY = 'qhse.permits';

/** Допустимое значение компонента в воздухе рабочей зоны: «не менее» и/или «не более» */
export const GasNorm = z
  .strictObject({
    component: Text(120),
    unit: z.string().trim().max(30),
    min: z.number().finite().nullable(),
    max: z.number().finite().nullable(),
  })
  .refine((v) => v.min !== null || v.max !== null, {
    message: 'Укажите допустимое значение: не менее или не более',
    path: ['max'],
  })
  .refine((v) => v.min === null || v.max === null || v.min <= v.max, {
    message: '«Не менее» больше, чем «не более»',
    path: ['min'],
  });
export type GasNorm = z.infer<typeof GasNorm>;

/** Правила вида работ: можно ли выдавать, какие удостоверения нужны бригаде, нужен ли согласующий ОТ и анализ воздуха */
export const PermitKindRule = z.strictObject({
  enabled: z.boolean(),
  requires: z.array(CredentialKindEnum).max(8),
  approver: z.boolean(),
  airControl: z.boolean(),
  minCrew: z.number().int().min(1).max(50),
  /** Нужны изоляции LOTO: без точек изоляции наряд не отправляется на подпись */
  loto: z.boolean(),
  /** Работа даёт огонь или искру: рядом в то же время с газоопасными или огневыми — нужно обоснование (SIMOPS) */
  ignition: z.boolean(),
  /** Нужен анализ рисков JSA: без него наряд не отправляется на подпись */
  jsa: z.boolean(),
});
export type PermitKindRule = z.infer<typeof PermitKindRule>;

export const PermitSettings = z
  .strictObject({
    kinds: z.partialRecord(PermitKindEnum, PermitKindRule),
    gas: z.array(GasNorm).max(40),
    /** Анализ перед открытием и возобновлением — не старше стольких минут */
    gasBeforeMinutes: z.number().int().min(5).max(240),
    /** Повторный анализ в ходе работ — не реже чем через столько минут; 0 — не требуется */
    gasRepeatMinutes: z.number().int().min(0).max(720),
    /** Наибольший срок наряда, дней */
    maxDays: z.number().int().min(1).max(31),
    /** Способы изоляции LOTO для формы */
    isolationMethods: z.array(Text(200)).min(1).max(20),
    /** Границы уровней риска JSA: с остаточным риском от «недопустимого» наряд не отправляется */
    riskLimits: RiskLimits,
  })
  .refine(
    (v) => {
      const keys = v.gas.map((g) => `${g.component.toLowerCase()}|${g.unit.toLowerCase()}`);
      return new Set(keys).size === keys.length;
    },
    { message: 'Компонент с той же единицей указан дважды', path: ['gas'] },
  );
export type PermitSettings = z.infer<typeof PermitSettings>;
/** Настройки в полном виде: у каждого вида работ есть правило */
export type PermitSettingsFull = Omit<PermitSettings, 'kinds'> & {
  kinds: Record<PermitKind, PermitKindRule>;
};

/**
 * Значения по умолчанию — ориентир, а не норма: допустимые концентрации и сроки анализа компания сверяет со своей
 * производственной инструкцией и гигиеническими нормативами воздуха рабочей зоны и правит в настройках.
 */
export const PERMIT_GAS_DEFAULTS: GasNorm[] = [
  { component: 'Горючие газы (метан)', unit: '% НКПР', min: null, max: 20 },
  { component: 'Сероводород (H₂S)', unit: 'мг/м³', min: null, max: 10 },
  { component: 'Оксид углерода (CO)', unit: 'мг/м³', min: null, max: 20 },
  { component: 'Кислород (O₂)', unit: '% об.', min: 20, max: 23 },
];

export const PERMIT_SETTINGS_DEFAULTS: PermitSettingsFull = {
  kinds: Object.fromEntries(
    (Object.keys(PERMIT_KINDS) as PermitKind[]).map((k) => [
      k,
      {
        enabled: true,
        requires: [...PERMIT_KINDS[k].requires],
        approver: PERMIT_KINDS[k].approver,
        airControl: k === 'gas' || k === 'hot',
        minCrew: k === 'gas' ? 2 : 1,
        loto: k === 'gas' || k === 'power_line',
        ignition: k === 'hot',
        jsa: k === 'gas' || k === 'hot',
      },
    ]),
  ) as Record<PermitKind, PermitKindRule>,
  gas: PERMIT_GAS_DEFAULTS,
  gasBeforeMinutes: 30,
  gasRepeatMinutes: 120,
  maxDays: 31,
  isolationMethods: PERMIT_ISOLATION_METHODS_DEFAULT,
  riskLimits: RISK_LIMITS_DEFAULT,
};

/** Сохранённые настройки поверх значений по умолчанию; испорченное значение заменяется значением по умолчанию */
export function permitSettingsOf(stored: unknown): PermitSettingsFull {
  const d = PERMIT_SETTINGS_DEFAULTS;
  const s = (stored && typeof stored === 'object' ? stored : {}) as Record<string, unknown>;
  const kinds = { ...d.kinds };
  const storedKinds = (s.kinds && typeof s.kinds === 'object' ? s.kinds : {}) as Record<string, unknown>;
  for (const k of Object.keys(kinds) as PermitKind[]) {
    // Правило, сохранённое до появления новых пунктов, дополняется значениями по умолчанию
    const raw = storedKinds[k];
    const r = PermitKindRule.safeParse(raw && typeof raw === 'object' ? { ...d.kinds[k], ...raw } : raw);
    if (r.success) kinds[k] = r.data;
  }
  const gas = z.array(GasNorm).max(40).safeParse(s.gas);
  const methods = PermitSettings.shape.isolationMethods.safeParse(s.isolationMethods);
  const limits = RiskLimits.safeParse(s.riskLimits);
  const int = (key: 'gasBeforeMinutes' | 'gasRepeatMinutes' | 'maxDays') => {
    const r = PermitSettings.shape[key].safeParse(s[key]);
    return r.success ? r.data : d[key];
  };
  return {
    kinds,
    gas: gas.success ? gas.data : d.gas,
    gasBeforeMinutes: int('gasBeforeMinutes'),
    gasRepeatMinutes: int('gasRepeatMinutes'),
    maxDays: int('maxDays'),
    isolationMethods: methods.success ? methods.data : d.isolationMethods,
    riskLimits: limits.success ? limits.data : d.riskLimits,
  };
}

/** Результат в допуске: на границе — ещё в норме */
export function gasWithin(result: number, norm: Pick<GasNorm, 'min' | 'max'>): boolean {
  return (norm.min === null || result >= norm.min) && (norm.max === null || result <= norm.max);
}

/** «≤ 20 % НКПР», «20–23 % об.» */
export function gasLimitLabel(norm: Pick<GasNorm, 'min' | 'max' | 'unit'>): string {
  const f = (n: number) => String(n).replace('.', ',');
  const range =
    norm.min !== null && norm.max !== null
      ? `${f(norm.min)}–${f(norm.max)}`
      : norm.max !== null
        ? `≤ ${f(norm.max)}`
        : `≥ ${f(norm.min!)}`;
  return `${range} ${norm.unit}`.trim();
}

export const GasTestInput = z.strictObject({
  /** Когда взята проба; по умолчанию — сейчас. Не в будущем и не старше суток */
  takenAt: Moment.nullable().optional(),
  /** Газоанализатор: модель, заводской номер */
  instrument: z.string().trim().max(200).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  rows: z
    .array(
      z.strictObject({
        component: Text(120),
        unit: z.string().trim().max(30),
        result: z.number().finite(),
      }),
    )
    .min(1)
    .max(40),
});
export type GasTestInput = z.infer<typeof GasTestInput>;

export interface PermitGasRow {
  id: string;
  component: string;
  unit: string;
  result: number;
  min: number | null;
  max: number | null;
  limit: string;
  ok: boolean;
  takenAt: string;
  takenByName: string;
  instrument: string | null;
}

/** Контроль воздуха по наряду */
export interface PermitGasView {
  /** Нужен ли анализ воздуха по виду работ (настройки компании) */
  required: boolean;
  /** not_required — не нужен; none — анализов нет; ok — в норме; bad — превышение; due — пора повторить */
  state: 'not_required' | 'none' | 'ok' | 'bad' | 'due';
  /** Последний результат по каждому компоненту */
  latest: PermitGasRow[];
  lastAt: string | null;
  /** Когда нужен следующий анализ (в ходе работ) */
  dueAt: string | null;
  /** Последние пробы, новые сверху */
  history: PermitGasRow[];
  /** Компоненты и допустимые значения для формы — из настроек компании */
  norms: Array<GasNorm & { limit: string }>;
  beforeMinutes: number;
  repeatMinutes: number;
}

export const PERMIT_EVENT_KINDS = {
  submitted: 'Отправлен на подпись',
  returned: 'Возвращён в работу',
  opened: 'Работы начаты',
  gas: 'Анализ воздуха',
  gas_exceeded: 'Превышение в анализе воздуха',
  suspended: 'Работы остановлены',
  resumed: 'Работы возобновлены',
  extended: 'Продлён',
  signed: 'Подпись',
  isolation: 'Изоляция',
  completed: 'Работы завершены',
  closed: 'Закрыт',
  cancelled: 'Отменён',
} as const;
export type PermitEventKind = keyof typeof PERMIT_EVENT_KINDS;

export interface PermitEventView {
  id: string;
  at: string;
  kind: PermitEventKind;
  userName: string | null;
  text: string | null;
}

export const PermitSuspendInput = z.strictObject({ reason: z.string().trim().min(3).max(500) });
export const PermitResumeInput = z.strictObject({ note: z.string().trim().max(500).nullable().optional() });

/** Результат проверки для плана дня площадки (поток 6): можно ли открывать работу */
export interface PermitCheck {
  ok: boolean;
  permit: { id: string; number: string; kind: PermitKind; validUntil: string } | null;
}

/* ─────────── Показатели нарядов-допусков (КЧ-05) ─────────── */

/** Показатели нарядов за период — считаются по журналу нарядов одной функцией `permitStats` */
export interface PermitStatsRow {
  /** Вид работ; `all` — все виды */
  kind: PermitKind | 'all';
  /** Нарядов, по которым что-то происходило в периоде */
  permits: number;
  submitted: number;
  returned: number;
  extended: number;
  gasExceeded: number;
  suspended: number;
  closed: number;
  /** Согласование: от последней отправки на подпись до последней подписи, в среднем, часов */
  approvalHours: number | null;
  approvalCount: number;
  /** Выполнение: от начала работ до «Работы завершены» (или закрытия), в среднем, часов */
  executionHours: number | null;
  executionCount: number;
}

export interface PermitStats {
  from: string;
  to: string;
  rows: PermitStatsRow[];
}

export const PermitStatsQuery = z.object({
  projectId: z.uuid().optional(),
  /** Начало периода, дата ГГГГ-ММ-ДД включительно */
  from: z.iso.date(),
  /** Конец периода, дата ГГГГ-ММ-ДД включительно */
  to: z.iso.date(),
});

/* ─────────── Инструктажи (КЧ-06) ─────────── */

export const BRIEFING_KINDS = {
  intro: 'Вводный',
  primary: 'Первичный на рабочем месте',
  repeat: 'Повторный',
  unscheduled: 'Внеплановый',
  targeted: 'Целевой',
} as const;
export type BriefingKind = keyof typeof BRIEFING_KINDS;
const BriefingKindEnum = z.enum(Object.keys(BRIEFING_KINDS) as [BriefingKind, ...BriefingKind[]]);

/** Основания внепланового инструктажа */
export const BRIEFING_REASONS = {
  rules: 'Новые правила или нормы',
  equipment: 'Смена оборудования',
  technology: 'Смена технологии',
  violation: 'Нарушение требований безопасности',
  accident: 'Несчастный случай или происшествие',
} as const;
export type BriefingReason = keyof typeof BRIEFING_REASONS;

/** Повторный инструктаж — не реже чем раз в три месяца */
export const BRIEFING_REPEAT_DAYS = 92;

export function briefingNumber(n: number): string {
  return `ИН-${String(n).padStart(4, '0')}`;
}

export const BriefingInput = z
  .strictObject({
    projectId: z.uuid(),
    objectId: z.uuid().nullable().optional(),
    permitId: z.uuid().nullable().optional(),
    kind: BriefingKindEnum,
    reason: z
      .enum(Object.keys(BRIEFING_REASONS) as [BriefingReason, ...BriefingReason[]])
      .nullable()
      .optional(),
    topic: Text(400),
    place: z.string().trim().max(300).nullable().optional(),
    heldOn: Day,
    conductorId: z.uuid(),
    workerIds: z.array(z.uuid()).min(1).max(200),
  })
  .refine((v) => v.kind !== 'unscheduled' || v.reason, {
    message: 'Внеплановый инструктаж проводится по основанию: укажите его',
    path: ['reason'],
  });
export type BriefingInput = z.infer<typeof BriefingInput>;

export interface BriefingAttendeeView {
  workerId: string;
  fullName: string;
  counterpartyName: string | null;
  hasAccount: boolean;
  signedAt: string | null;
  /** app — подписал сам в приложении; paper — подпись на бумаге отметил проводивший */
  method: 'app' | 'paper' | null;
  isMe: boolean;
}

export interface BriefingSummary {
  id: string;
  number: string;
  projectId: string;
  projectCode: string;
  kind: BriefingKind;
  topic: string;
  heldOn: string;
  conductorName: string;
  status: 'open' | 'closed';
  attendees: number;
  signed: number;
}

export interface BriefingDetail extends BriefingSummary {
  reason: BriefingReason | null;
  place: string | null;
  objectId: string | null;
  permitId: string | null;
  permitNumber: string | null;
  conductorId: string;
  nextDueOn: string | null;
  closedAt: string | null;
  attendeeList: BriefingAttendeeView[];
  can: { edit: boolean; close: boolean; signAnyone: boolean };
}

export interface BriefingJournalRow {
  number: string;
  heldOn: string;
  kind: BriefingKind;
  topic: string;
  reason: BriefingReason | null;
  workerName: string;
  counterpartyName: string | null;
  conductorName: string;
  signedAt: string | null;
  method: 'app' | 'paper' | null;
}

export interface BriefingDueRow {
  workerId: string;
  fullName: string;
  counterpartyName: string | null;
  /** Дата последнего подписанного вводного/первичного/повторного; пусто — не проходил */
  lastOn: string | null;
  dueOn: string;
  overdue: boolean;
}

/* ─────────── Наблюдения за безопасностью (КЧ-09) ─────────── */

export const OBSERVATION_CATEGORIES = {
  unsafe_act: 'Опасное действие',
  unsafe_condition: 'Опасное состояние',
  near_miss: 'Чуть не случилось',
  good_practice: 'Хороший пример',
} as const;
export type ObservationCategory = keyof typeof OBSERVATION_CATEGORIES;

export const OBSERVATION_STATUSES = {
  new: 'Новое',
  measures: 'Меры приняты в работу',
  closed: 'Закрыто',
  rejected: 'Отклонено',
} as const;
export type ObservationStatus = keyof typeof OBSERVATION_STATUSES;

export const OBSERVATION_SEVERITIES = { low: 'Низкая', medium: 'Средняя', high: 'Высокая' } as const;
export type ObservationSeverity = keyof typeof OBSERVATION_SEVERITIES;

export function observationNumber(n: number): string {
  return `НБ-${String(n).padStart(4, '0')}`;
}

export const OBSERVATION_MAX_PHOTOS = 5;

export const ObservationReviewInput = z
  .strictObject({
    severity: z.enum(['low', 'medium', 'high']),
    note: z.string().trim().max(2000).nullable().optional(),
    /** Меры: что сделать, кто и к какому дню. Без мер наблюдение закрывается «к сведению» */
    measure: z.string().trim().min(1).max(2000).nullable().optional(),
    assigneeId: z.uuid().nullable().optional(),
    dueDate: Day.nullable().optional(),
  })
  .refine((v) => !v.measure || (v.assigneeId && v.dueDate), {
    message: 'К мерам нужны ответственный и срок',
    path: ['assigneeId'],
  });
export type ObservationReviewInput = z.infer<typeof ObservationReviewInput>;

export interface ObservationRow {
  id: string;
  number: string;
  projectId: string;
  projectCode: string;
  category: ObservationCategory;
  place: string | null;
  description: string;
  status: ObservationStatus;
  severity: ObservationSeverity | null;
  /** Автор — только у не анонимных; анонимное наблюдение автора не хранит */
  anonymous: boolean;
  authorName: string | null;
  createdAt: string;
  photos: Array<{ id: string; name: string }>;
  measure: string | null;
  measureDueDate: string | null;
  assigneeName: string | null;
  reviewNote: string | null;
  closeNote: string | null;
  overdue: boolean;
  can: { review: boolean; done: boolean; reject: boolean };
}

/** Сводка по проекту для карточки проекта и панели раздела */
export interface SafetySummary {
  activePermits: number;
  expiredPermits: number;
  /** Работы остановлены (превышение в анализе воздуха или вручную) */
  suspendedPermits: number;
  credentialsExpired: number;
  credentialsExpiring: number;
  briefingsOpen: number;
  repeatOverdue: number;
  observationsNew: number;
  observationsOverdue: number;
}
